/**
 * Order receipt actions — show QR code, Download PDF, Print.
 * Used on the admin/technician order detail page so the operator can
 * hand a paper receipt + tracking QR to the customer.
 *
 * Renders only when the order has a `release_code` assigned (i.e. the
 * order was successfully created and the public track page is live).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { CardNB, ButtonNB, IconNB } from '../ui-nb';
import { generateTrackQR } from '../../lib/qr-code';
import { exportOrderPDF, printOrderReceipt, type OrderReceiptData } from '../../lib/order-pdf';
import { useToast } from '../ToastNB';
import { error as logError } from '../../lib/logger';
import { getErrorMessage } from '../../lib/errors';

interface OrderReceiptActionsProps {
  order: OrderReceiptData;
}

export default function OrderReceiptActions({ order }: OrderReceiptActionsProps) {
  const { t, language } = useTranslation();
  const { addToast } = useToast();
  const [qrDataURL, setQrDataURL] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!order.release_code) return;
    generateTrackQR(order.release_code, language)
      .then((url) => {
        if (!cancelled) setQrDataURL(url);
      })
      .catch((err) => {
        logError('Failed to generate tracking QR', { error: getErrorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [order.release_code, language]);

  if (!order.release_code) return null;

  const handleDownload = async () => {
    setPdfLoading(true);
    try {
      const qr = qrDataURL ?? (await generateTrackQR(order.release_code, language));
      await exportOrderPDF(order, language, qr, t);
      addToast(t('orderReceipt.downloaded'), 'success');
    } catch (err) {
      logError('PDF download failed', { error: getErrorMessage(err) });
      addToast(t('orderReceipt.failed'), 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const qr = qrDataURL ?? (await generateTrackQR(order.release_code, language));
      printOrderReceipt(order, language, qr, t);
      addToast(t('orderReceipt.printing'), 'info');
    } catch (err) {
      logError('Print failed', { error: getErrorMessage(err) });
      addToast(t('orderReceipt.failed'), 'error');
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <CardNB className="p-6">
      <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">
        {t('orderReceipt.title')}
      </h2>
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {qrDataURL && (
          <div className="flex-shrink-0 bg-white p-3 border-3 border-nb-black rounded-[2px]">
            <img
              src={qrDataURL}
              alt="Order tracking QR"
              width={160}
              height={160}
              className="block"
            />
          </div>
        )}
        <div className="flex-1 flex flex-col gap-3 w-full sm:w-auto">
          <p className="text-sm text-nb-text-muted text-center sm:text-left">
            {t('orderReceipt.scanToTrack')}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <ButtonNB
              variant="primary"
              size="md"
              onClick={handleDownload}
              disabled={pdfLoading || printLoading}
            >
              <IconNB name="download" size={16} />
              {pdfLoading ? '...' : t('orderReceipt.downloadPdf')}
            </ButtonNB>
            <ButtonNB
              variant="secondary"
              size="md"
              onClick={handlePrint}
              disabled={pdfLoading || printLoading}
            >
              <IconNB name="printer" size={16} />
              {printLoading ? '...' : t('orderReceipt.print')}
            </ButtonNB>
          </div>
        </div>
      </div>
    </CardNB>
  );
}
